import { SelectInputField, TextInputField } from "@/shared/ui/composites";
import { Badge, Button, HStack, Modal, TextareaBox, VStack } from "@/shared/ui/primitives";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
	WorkflowCanvasActionData,
	WorkflowCanvasControlData,
	WorkflowCanvasEdge,
	WorkflowCanvasElement,
} from "./workflow-canvas.types";

interface NodePropertyModalProps {
	isOpen: boolean;
	element: WorkflowCanvasElement | null;
	elements: WorkflowCanvasElement[];
	edges: WorkflowCanvasEdge[];
	onClose: () => void;
	onSave: (element: WorkflowCanvasElement) => void;
}

const PARAMETER_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/;

export const NodePropertyModal = ({
	isOpen,
	element,
	elements,
	edges,
	onClose,
	onSave,
}: NodePropertyModalProps) => {
	const { t } = useTranslation();
	const [draft, setDraft] = useState<WorkflowCanvasElement | null>(element);

	useEffect(() => {
		setDraft(element);
	}, [element]);

	const parameterNameError = useMemo(() => {
		if (!draft) return null;

		const parameterName = draft.data.parameterName.trim();
		if (!parameterName) {
			return t("workflow.node.paramNameRequired");
		}

		if (!PARAMETER_NAME_PATTERN.test(parameterName)) {
			return t("workflow.node.paramNamePattern");
		}

		const duplicated = elements.some(
			(elementItem) =>
				elementItem.id !== draft.id && elementItem.data.parameterName.trim() === parameterName,
		);

		if (duplicated) {
			return t("workflow.node.paramNameExists");
		}

		return null;
	}, [draft, elements]);

	const requestUrlError = useMemo(() => {
		if (!draft || draft.kind !== "action") return null;
		if (draft.data.moduleType !== "http" && draft.data.moduleType !== "crawl") return null;

		if (!draft.data.url?.trim()) {
			return t("workflow.node.urlRequired");
		}

		return null;
	}, [draft]);

	const incomingParameters = useMemo(() => {
		if (!draft) {
			return [] as { key: string; fromNode: string; edgeLabel?: string; value: string }[];
		}

		const result: { key: string; fromNode: string; edgeLabel?: string; value: string }[] = [];

		for (const edge of edges) {
			if (edge.toElementId !== draft.id) {
				continue;
			}

			const fromNode = elements.find((elementItem) => elementItem.id === edge.fromElementId);
			if (!fromNode) {
				continue;
			}

			const parentParam = fromNode.data.parameterName;
			const key = edge.label ? `${parentParam}.${edge.label}` : parentParam;

			result.push({
				key,
				fromNode: fromNode.data.name,
				edgeLabel: edge.label,
				value: `inputs.${key}`,
			});
		}

		return result;
	}, [draft, edges, elements]);

	const isSaveDisabled = !draft || Boolean(parameterNameError) || Boolean(requestUrlError);

	const handleAttemptClose = () => {
		if (isSaveDisabled) {
			return;
		}

		onClose();
	};

	const handleSave = () => {
		if (!draft || isSaveDisabled) {
			return;
		}

		if (draft.kind === "action") {
			onSave({
				...draft,
				data: {
					...draft.data,
					name: draft.data.name.trim(),
					description: draft.data.description.trim(),
					parameterName: draft.data.parameterName.trim(),
				},
			});
			return;
		}

		onSave({
			...draft,
			data: {
				...draft.data,
				name: draft.data.name.trim(),
				description: draft.data.description.trim(),
				parameterName: draft.data.parameterName.trim(),
			},
		});
	};

	const updateActionData = (
		updater: (data: WorkflowCanvasActionData) => WorkflowCanvasActionData,
	) => {
		setDraft((current) => {
			if (!current || current.kind !== "action") {
				return current;
			}

			return {
				...current,
				data: updater(current.data),
			};
		});
	};

	const updateControlData = (
		updater: (data: WorkflowCanvasControlData) => WorkflowCanvasControlData,
	) => {
		setDraft((current) => {
			if (!current || current.kind !== "control") {
				return current;
			}

			return {
				...current,
				data: updater(current.data),
			};
		});
	};

	return (
		<Modal className="w-full max-w-[460px]" isOpen={isOpen} onClose={handleAttemptClose}>
			<VStack className="w-full max-h-[80vh] rounded-[20px] bg-bg-weak outline-outline p-[20px] gap-[6px] items-start">
				<span className="text-xl font-semibold text-ct-strong p-[4px]">
					{draft?.data.name ?? t("common.edit")}
				</span>

				<VStack className="w-full gap-[10px] items-start overflow-auto justify-start">
					<VStack className="w-full items-start">
						<span className="text-sm text-ct-strong font-medium p-[6px]">{t("workflow.node.incomingParams")}</span>
						<VStack className="w-full gap-[6px] items-start rounded-[10px] bg-bg-weak border-outline p-[10px]">
							{incomingParameters.length === 0 ? (
								<span className="text-sm text-ct-regular px-[4px]">{t("workflow.node.noIncomingParams")}</span>
							) : (
								<HStack className="w-full flex-wrap justify-start gap-[8px]">
									{incomingParameters.map((item) => (
										<Badge key={item.key}>{item.key}</Badge>
									))}
								</HStack>
							)}
						</VStack>
					</VStack>

					<TextInputField
						label={t("workflow.node.parameterName")}
						value={draft?.data.parameterName ?? ""}
						onChange={(event) => {
							const value = event.target.value;
							setDraft((current) => {
								if (!current) {
									return current;
								}

								if (current.kind === "action") {
									return {
										...current,
										data: {
											...current.data,
											parameterName: value,
										},
									};
								}

								return {
									...current,
									data: {
										...current.data,
										parameterName: value,
									},
								};
							});
						}}
						placeholder="ex) ai-1"
						errorMessage={parameterNameError ?? undefined}
						clearable
					/>

					<VStack className="w-full gap-[6px] items-start">
						<span className="text-sm text-ct-strong font-medium px-[6px]">{t("workflow.node.description")}</span>
						<TextareaBox
							rows={3}
							value={draft?.data.description ?? ""}
							onChange={(event) => {
								const value = event.target.value;
								setDraft((current) => {
									if (!current) {
										return current;
									}

									if (current.kind === "action") {
										return {
											...current,
											data: {
												...current.data,
												description: value,
											},
										};
									}

									return {
										...current,
										data: {
											...current.data,
											description: value,
										},
									};
								});
							}}
							placeholder={t("workflow.node.description")}
							className="w-full"
						/>
					</VStack>

					{draft?.kind === "action" && draft.data.moduleType === "http" && (
						<>
							<SelectInputField
								label={t("workflow.node.httpMethod")}
								value={draft.data.method ?? "GET"}
								onChange={(value: string) =>
									updateActionData((data) => ({
										...data,
										method: value as WorkflowCanvasActionData["method"],
									}))
								}
								options={[
									{ label: "GET", value: "GET" },
									{ label: "POST", value: "POST" },
									{ label: "PUT", value: "PUT" },
									{ label: "PATCH", value: "PATCH" },
									{ label: "DELETE", value: "DELETE" },
								]}
							/>
							<TextInputField
								label={t("workflow.node.url")}
								value={draft.data.url ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({
										...data,
										url: event.target.value,
									}))
								}
								placeholder="https://api.example.com"
								errorMessage={requestUrlError ?? undefined}
								clearable
							/>
							<TextInputField
								label={t("workflow.node.headers")}
								value={draft.data.headers ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({
										...data,
										headers: event.target.value,
									}))
								}
								placeholder='{"Authorization":"Bearer ..."}'
								clearable
							/>
							<TextInputField
								label={t("workflow.node.body")}
								value={draft.data.body ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({
										...data,
										body: event.target.value,
									}))
								}
								placeholder='{"key":"value"}'
								clearable
							/>
							<TextInputField
								label={t("workflow.node.timeoutMs")}
								type="number"
								value={draft.data.timeout ?? 30000}
								onChange={(event) =>
									updateActionData((data) => ({
										...data,
										timeout: Number(event.target.value),
									}))
								}
								placeholder="30000"
							/>
						</>
					)}

					{draft?.kind === "action" && draft.data.moduleType === "sms" && (
						<>
							<TextInputField
								label={t("workflow.node.phoneNumber")}
								value={draft.data.phoneNumber ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({ ...data, phoneNumber: event.target.value }))
								}
								placeholder="+82..."
								clearable
							/>
							<TextInputField
								label={t("workflow.node.message")}
								value={draft.data.message ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({ ...data, message: event.target.value }))
								}
								placeholder={t("workflow.node.smsMessage")}
								clearable
							/>
						</>
					)}

					{draft?.kind === "action" && draft.data.moduleType === "gmail" && (
						<>
							<TextInputField
								label={t("workflow.node.to")}
								value={draft.data.to ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({ ...data, to: event.target.value }))
								}
								placeholder="to@example.com"
								clearable
							/>
							<TextInputField
								label={t("workflow.node.subject")}
								value={draft.data.subject ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({ ...data, subject: event.target.value }))
								}
								placeholder={t("workflow.node.mailSubject")}
								clearable
							/>
							<TextInputField
								label={t("workflow.node.body")}
								value={draft.data.body ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({ ...data, body: event.target.value }))
								}
								placeholder={t("workflow.node.emailBody")}
								clearable
							/>
							<TextInputField
								label={t("workflow.node.html")}
								value={draft.data.html ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({ ...data, html: event.target.value }))
								}
								placeholder="<p>...</p>"
								clearable
							/>
							<TextInputField
								label={t("workflow.node.attachments")}
								value={draft.data.attachments ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({ ...data, attachments: event.target.value }))
								}
								placeholder={t("workflow.node.jsonArrayOrLinks")}
								clearable
							/>
						</>
					)}

					{draft?.kind === "action" && draft.data.moduleType === "google-drive" && (
						<>
							<SelectInputField
								label={t("workflow.node.driveOperation")}
								value={draft.data.googleDriveOperation ?? "list"}
								onChange={(value: string) =>
									updateActionData((data) => ({
										...data,
										googleDriveOperation: value as WorkflowCanvasActionData["googleDriveOperation"],
									}))
								}
								options={[
									{ label: t("workflow.node.optList"), value: "list" },
									{ label: t("workflow.node.optUpload"), value: "upload" },
									{ label: t("workflow.node.optDownload"), value: "download" },
									{ label: t("workflow.node.optDelete"), value: "delete" },
									{ label: t("workflow.node.optCreateFolder"), value: "createFolder" },
								]}
							/>

							{draft.data.googleDriveOperation === "list" ? (
								<TextInputField
									label={t("workflow.node.folderId")}
									value={draft.data.folderId ?? ""}
									onChange={(event) =>
										updateActionData((data) => ({ ...data, folderId: event.target.value }))
									}
									placeholder="folderId"
									clearable
								/>
							) : null}

							{draft.data.googleDriveOperation === "upload" ? (
								<>
									<TextInputField
										label={t("workflow.node.folderId")}
										value={draft.data.folderId ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, folderId: event.target.value }))
										}
										placeholder="folderId"
										clearable
									/>
									<TextInputField
										label={t("workflow.node.fileName")}
										value={draft.data.fileName ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, fileName: event.target.value }))
										}
										placeholder="file.txt"
										clearable
									/>
									<TextInputField
										label={t("workflow.node.content")}
										value={draft.data.content ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, content: event.target.value }))
										}
										placeholder={t("workflow.node.contentBase64")}
										clearable
									/>
									<TextInputField
										label={t("workflow.node.mimeType")}
										value={draft.data.mimeType ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, mimeType: event.target.value }))
										}
										placeholder="text/plain"
										clearable
									/>
								</>
							) : null}

							{draft.data.googleDriveOperation === "download" ||
							draft.data.googleDriveOperation === "delete" ? (
								<TextInputField
									label={t("workflow.node.fileId")}
									value={draft.data.fileId ?? ""}
									onChange={(event) =>
										updateActionData((data) => ({ ...data, fileId: event.target.value }))
									}
									placeholder="fileId"
									clearable
								/>
							) : null}

							{draft.data.googleDriveOperation === "createFolder" ? (
								<>
									<TextInputField
										label={t("workflow.node.parentFolderId")}
										value={draft.data.folderId ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, folderId: event.target.value }))
										}
										placeholder="folderId"
										clearable
									/>
									<TextInputField
										label={t("workflow.node.folderName")}
										value={draft.data.fileName ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, fileName: event.target.value }))
										}
										placeholder={t("workflow.node.newFolder")}
										clearable
									/>
								</>
							) : null}
						</>
					)}

					{draft?.kind === "action" && draft.data.moduleType === "google-sheet" && (
						<>
							<SelectInputField
								label={t("workflow.node.sheetOperation")}
								value={draft.data.googleSheetOperation ?? "read"}
								onChange={(value: string) =>
									updateActionData((data) => ({
										...data,
										googleSheetOperation: value as WorkflowCanvasActionData["googleSheetOperation"],
									}))
								}
								options={[
									{ label: t("workflow.node.optRead"), value: "read" },
									{ label: t("workflow.node.optWrite"), value: "write" },
									{ label: t("workflow.node.optAppend"), value: "append" },
									{ label: t("workflow.node.optCreate"), value: "create" },
								]}
							/>

							{draft.data.googleSheetOperation === "read" ||
							draft.data.googleSheetOperation === "write" ||
							draft.data.googleSheetOperation === "append" ? (
								<>
									<TextInputField
										label={t("workflow.node.spreadsheetId")}
										value={draft.data.spreadsheetId ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, spreadsheetId: event.target.value }))
										}
										placeholder="spreadsheetId"
										clearable
									/>
									<TextInputField
										label={t("workflow.node.range")}
										value={draft.data.range ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, range: event.target.value }))
										}
										placeholder="Sheet1!A1:B10"
										clearable
									/>
								</>
							) : null}

							{draft.data.googleSheetOperation === "write" ||
							draft.data.googleSheetOperation === "append" ? (
								<TextInputField
									label={t("workflow.node.values")}
									value={draft.data.values ?? ""}
									onChange={(event) =>
										updateActionData((data) => ({ ...data, values: event.target.value }))
									}
									placeholder='[["a","b"]]'
									clearable
								/>
							) : null}

							{draft.data.googleSheetOperation === "create" ? (
								<>
									<TextInputField
										label={t("workflow.node.title")}
										value={draft.data.title ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, title: event.target.value }))
										}
										placeholder={t("workflow.node.newSheet")}
										clearable
									/>
									<TextInputField
										label={t("workflow.node.values")}
										value={draft.data.values ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, values: event.target.value }))
										}
										placeholder='[["a","b"]]'
										clearable
									/>
								</>
							) : null}
						</>
					)}

					{draft?.kind === "action" && draft.data.moduleType === "ai" && (
						<>
							<SelectInputField
								label={t("workflow.node.aiResponseType")}
								value={draft.data.aiResponseType ?? "natural"}
								onChange={(value: string) =>
									updateActionData((data) => ({
										...data,
										aiResponseType: value as WorkflowCanvasActionData["aiResponseType"],
									}))
								}
								options={[
									{ label: t("workflow.node.optNatural"), value: "natural" },
									{ label: t("workflow.node.optJson"), value: "json" },
									{ label: t("workflow.node.optMusic"), value: "music" },
									{ label: t("workflow.node.optTts"), value: "tts" },
									{ label: t("workflow.node.optImage"), value: "image" },
								]}
							/>

							{draft.data.aiResponseType === "natural" ? (
								<>
									<TextInputField
										label={t("workflow.node.prompt")}
										value={draft.data.aiPrompt ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, aiPrompt: event.target.value }))
										}
										placeholder={t("workflow.node.prompt")}
										clearable
									/>
									<TextInputField
										label={t("workflow.node.context")}
										value={draft.data.aiContext ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, aiContext: event.target.value }))
										}
										placeholder={t("workflow.node.context")}
										clearable
									/>
									<TextInputField
										label={t("workflow.node.maxTokens")}
										type="number"
										value={draft.data.aiMaxTokens ?? 1024}
										onChange={(event) =>
											updateActionData((data) => ({
												...data,
												aiMaxTokens: Number(event.target.value),
											}))
										}
										placeholder="1024"
									/>
								</>
							) : null}

							{draft.data.aiResponseType === "json" ? (
								<>
									<TextInputField
										label={t("workflow.node.prompt")}
										value={draft.data.aiPrompt ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, aiPrompt: event.target.value }))
										}
										placeholder={t("workflow.node.prompt")}
										clearable
									/>
									<TextInputField
										label={t("workflow.node.schema")}
										value={draft.data.aiSchema ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, aiSchema: event.target.value }))
										}
										placeholder='{"type":"object"}'
										clearable
									/>
									<TextInputField
										label={t("workflow.node.context")}
										value={draft.data.aiContext ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, aiContext: event.target.value }))
										}
										placeholder={t("workflow.node.context")}
										clearable
									/>
								</>
							) : null}

							{draft.data.aiResponseType === "music" ? (
								<>
									<TextInputField
										label={t("workflow.node.prompt")}
										value={draft.data.aiPrompt ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, aiPrompt: event.target.value }))
										}
										placeholder={t("workflow.node.prompt")}
										clearable
									/>
									<TextInputField
										label={t("workflow.node.durationSec")}
										type="number"
										value={draft.data.aiDurationSeconds ?? 30}
										onChange={(event) =>
											updateActionData((data) => ({
												...data,
												aiDurationSeconds: Number(event.target.value),
											}))
										}
										placeholder="30"
									/>
								</>
							) : null}

							{draft.data.aiResponseType === "tts" ? (
								<>
									<TextInputField
										label={t("workflow.node.text")}
										value={draft.data.aiText ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, aiText: event.target.value }))
										}
										placeholder={t("workflow.node.speechText")}
										clearable
									/>
									<TextInputField
										label={t("workflow.node.voice")}
										value={draft.data.aiVoice ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, aiVoice: event.target.value }))
										}
										placeholder="alloy"
										clearable
									/>
								</>
							) : null}

							{draft.data.aiResponseType === "image" ? (
								<>
									<TextInputField
										label={t("workflow.node.prompt")}
										value={draft.data.aiPrompt ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, aiPrompt: event.target.value }))
										}
										placeholder={t("workflow.node.imagePrompt")}
										clearable
									/>
									<TextInputField
										label={t("workflow.node.size")}
										value={draft.data.aiSize ?? "1024x1024"}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, aiSize: event.target.value }))
										}
										placeholder="1024x1024"
										clearable
									/>
								</>
							) : null}
						</>
					)}

					{draft?.kind === "action" && draft.data.moduleType === "google-form" && (
						<>
							<SelectInputField
								label={t("workflow.node.formOperation")}
								value={draft.data.googleFormOperation ?? "create"}
								onChange={(value: string) =>
									updateActionData((data) => ({
										...data,
										googleFormOperation: value as WorkflowCanvasActionData["googleFormOperation"],
									}))
								}
								options={[
									{ label: t("workflow.node.optCreate"), value: "create" },
									{ label: t("workflow.node.optEdit"), value: "edit" },
									{ label: t("workflow.node.optDelete"), value: "delete" },
									{ label: t("workflow.node.optRead"), value: "read" },
									{ label: t("workflow.node.optResponses"), value: "responses" },
								]}
							/>

							{draft.data.googleFormOperation !== "create" ? (
								<TextInputField
									label={t("workflow.node.formId")}
									value={draft.data.googleFormId ?? ""}
									onChange={(event) =>
										updateActionData((data) => ({
											...data,
											googleFormId: event.target.value,
										}))
									}
									placeholder="Google Form ID"
									clearable
								/>
							) : null}

							{draft.data.googleFormOperation === "create" ||
							draft.data.googleFormOperation === "edit" ? (
								<>
									<TextInputField
										label={t("workflow.node.formTitle")}
										value={draft.data.googleFormTitle ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({
												...data,
												googleFormTitle: event.target.value,
											}))
										}
										placeholder={t("workflow.node.customerFeedback")}
										clearable
									/>
									<TextInputField
										label={t("workflow.node.formDescription")}
										value={draft.data.googleFormDescription ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({
												...data,
												googleFormDescription: event.target.value,
											}))
										}
										placeholder={t("workflow.node.shortFormDesc")}
										clearable
									/>
									<TextInputField
										label={t("workflow.node.items")}
										value={draft.data.googleFormItems ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({
												...data,
												googleFormItems: event.target.value,
											}))
										}
										placeholder={t("workflow.node.jsonItems")}
										clearable
									/>
									<TextInputField
										label={t("workflow.node.settings")}
										value={draft.data.googleFormSettings ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({
												...data,
												googleFormSettings: event.target.value,
											}))
										}
										placeholder={t("workflow.node.jsonSettings")}
										clearable
									/>
								</>
							) : null}
						</>
					)}

					{draft?.kind === "action" && draft.data.moduleType === "youtube" && (
						<>
							<TextInputField
								label={t("workflow.node.videoUrl")}
								value={draft.data.youtubeVideoUrl ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({
										...data,
										youtubeVideoUrl: event.target.value,
									}))
								}
								placeholder="https://..."
								clearable
							/>
							<TextInputField
								label={t("workflow.node.title")}
								value={draft.data.youtubeTitle ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({
										...data,
										youtubeTitle: event.target.value,
									}))
								}
								placeholder={t("workflow.node.videoTitle")}
								clearable
							/>
							<TextInputField
								label={t("workflow.node.description")}
								value={draft.data.youtubeDescription ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({
										...data,
										youtubeDescription: event.target.value,
									}))
								}
								placeholder={t("workflow.node.videoDescription")}
								clearable
							/>
							<TextInputField
								label={t("workflow.node.tags")}
								value={draft.data.youtubeTags ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({
										...data,
										youtubeTags: event.target.value,
									}))
								}
								placeholder="tag1,tag2"
								clearable
							/>
							<TextInputField
								label={t("workflow.node.categoryId")}
								value={draft.data.youtubeCategoryId ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({
										...data,
										youtubeCategoryId: event.target.value,
									}))
								}
								placeholder="22"
								clearable
							/>
							<SelectInputField
								label={t("workflow.node.privacy")}
								value={draft.data.youtubePrivacyStatus ?? "private"}
								onChange={(value: string) =>
									updateActionData((data) => ({
										...data,
										youtubePrivacyStatus: value as WorkflowCanvasActionData["youtubePrivacyStatus"],
									}))
								}
								options={[
									{ label: t("workflow.node.optPrivate"), value: "private" },
									{ label: t("workflow.node.optUnlisted"), value: "unlisted" },
									{ label: t("workflow.node.optPublic"), value: "public" },
								]}
							/>
							<SelectInputField
								label={t("workflow.node.madeForKids")}
								value={draft.data.youtubeMadeForKids ? "true" : "false"}
								onChange={(value: string) =>
									updateActionData((data) => ({
										...data,
										youtubeMadeForKids: value === "true",
									}))
								}
								options={[
									{ label: t("workflow.node.optNo"), value: "false" },
									{ label: t("workflow.node.optYes"), value: "true" },
								]}
							/>
						</>
					)}

					{draft?.kind === "action" && draft.data.moduleType === "transform" && (
						<>
							<SelectInputField
								label={t("workflow.node.from")}
								value={draft.data.transformFrom ?? "json"}
								onChange={(value: string) =>
									updateActionData((data) => ({
										...data,
										transformFrom: value as WorkflowCanvasActionData["transformFrom"],
									}))
								}
								options={[
									{ label: "JSON", value: "json" },
									{ label: "CSV", value: "csv" },
									{ label: "HTML", value: "html" },
									{ label: "XML", value: "xml" },
								]}
							/>
							<SelectInputField
								label={t("workflow.node.toFormat")}
								value={draft.data.transformTo ?? "json"}
								onChange={(value: string) =>
									updateActionData((data) => ({
										...data,
										transformTo: value as WorkflowCanvasActionData["transformTo"],
									}))
								}
								options={[
									{ label: "JSON", value: "json" },
									{ label: "CSV", value: "csv" },
									{ label: "HTML", value: "html" },
									{ label: "XML", value: "xml" },
								]}
							/>
							<TextInputField
								label={t("workflow.node.data")}
								value={draft.data.transformData ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({ ...data, transformData: event.target.value }))
								}
								placeholder={t("workflow.node.inputData")}
								clearable
							/>
							<TextInputField
								label={t("workflow.node.options")}
								value={draft.data.transformOptions ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({ ...data, transformOptions: event.target.value }))
								}
								placeholder={t("workflow.node.jsonOptions")}
								clearable
							/>
						</>
					)}

					{draft?.kind === "action" && draft.data.moduleType === "latex" && (
						<>
							<TextInputField
								label={t("workflow.node.expression")}
								value={draft.data.latexExpression ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({ ...data, latexExpression: event.target.value }))
								}
								placeholder="x^2 + y"
								clearable
							/>
							<TextInputField
								label={t("workflow.node.variables")}
								value={draft.data.latexVariables ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({ ...data, latexVariables: event.target.value }))
								}
								placeholder='{"x":1,"y":2}'
								clearable
							/>
						</>
					)}

					{draft?.kind === "action" && draft.data.moduleType === "crawl" && (
						<>
							<TextInputField
								label={t("workflow.node.url")}
								value={draft.data.url ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({ ...data, url: event.target.value }))
								}
								placeholder="https://example.com"
								clearable
							/>
							<TextInputField
								label={t("workflow.node.selector")}
								value={draft.data.crawlSelector ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({ ...data, crawlSelector: event.target.value }))
								}
								placeholder=".content"
								clearable
							/>
							<TextInputField
								label={t("workflow.node.extract")}
								value={draft.data.crawlExtract ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({ ...data, crawlExtract: event.target.value }))
								}
								placeholder={t("workflow.node.extractHint")}
								clearable
							/>
							<TextInputField
								label={t("workflow.node.timeoutMs")}
								type="number"
								value={draft.data.timeout ?? 15000}
								onChange={(event) =>
									updateActionData((data) => ({ ...data, timeout: Number(event.target.value) }))
								}
								placeholder="15000"
							/>
						</>
					)}

					{draft?.kind === "action" && draft.data.moduleType === "db" && (
						<>
							<SelectInputField
								label={t("workflow.node.dbOperation")}
								value={draft.data.dbOperation ?? "read"}
								onChange={(value: string) =>
									updateActionData((data) => ({
										...data,
										dbOperation: value as WorkflowCanvasActionData["dbOperation"],
									}))
								}
								options={[
									{ label: t("workflow.node.optRead"), value: "read" },
									{ label: t("workflow.node.optWrite"), value: "write" },
									{ label: t("workflow.node.optUpdate"), value: "update" },
									{ label: t("workflow.node.optDelete"), value: "delete" },
								]}
							/>
							{draft.data.dbOperation === "read" ? (
								<>
									<TextInputField
										label={t("workflow.node.ref")}
										value={draft.data.dbRef ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, dbRef: event.target.value }))
										}
										placeholder={t("workflow.node.refPath")}
										clearable
									/>
									<TextInputField
										label={t("workflow.node.select")}
										value={draft.data.dbSelect ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, dbSelect: event.target.value }))
										}
										placeholder="field1,field2"
										clearable
									/>
								</>
							) : null}
							{draft.data.dbOperation === "write" ? (
								<>
									<TextInputField
										label={t("workflow.node.table")}
										value={draft.data.dbTable ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, dbTable: event.target.value }))
										}
										placeholder="table"
										clearable
									/>
									<TextInputField
										label={t("workflow.node.data")}
										value={draft.data.dbData ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, dbData: event.target.value }))
										}
										placeholder='{"key":"value"}'
										clearable
									/>
								</>
							) : null}
							{draft.data.dbOperation === "update" ? (
								<TextInputField
									label={t("workflow.node.sets")}
									value={draft.data.dbSets ?? ""}
									onChange={(event) =>
										updateActionData((data) => ({ ...data, dbSets: event.target.value }))
									}
									placeholder='[{"path":"a","value":1}]'
									clearable
								/>
							) : null}
							{draft.data.dbOperation === "delete" ? (
								<>
									<TextInputField
										label={t("workflow.node.table")}
										value={draft.data.dbTable ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, dbTable: event.target.value }))
										}
										placeholder="table"
										clearable
									/>
									<TextInputField
										label={t("workflow.node.ref")}
										value={draft.data.dbRef ?? ""}
										onChange={(event) =>
											updateActionData((data) => ({ ...data, dbRef: event.target.value }))
										}
										placeholder={t("workflow.node.refPath")}
										clearable
									/>
								</>
							) : null}
						</>
					)}

					{draft?.kind === "action" && draft.data.moduleType === "env" && (
						<>
							<SelectInputField
								label={t("workflow.node.envOperation")}
								value={draft.data.envOperation ?? "get"}
								onChange={(value: string) =>
									updateActionData((data) => ({
										...data,
										envOperation: value as WorkflowCanvasActionData["envOperation"],
									}))
								}
								options={[
									{ label: t("workflow.node.optGet"), value: "get" },
									{ label: t("workflow.node.optSet"), value: "set" },
								]}
							/>
							<TextInputField
								label={t("workflow.node.key")}
								value={draft.data.envKey ?? ""}
								onChange={(event) =>
									updateActionData((data) => ({ ...data, envKey: event.target.value }))
								}
								placeholder="ENV_KEY"
								clearable
							/>
							{draft.data.envOperation === "set" ? (
								<TextInputField
									label={t("workflow.node.value")}
									value={draft.data.envValue ?? ""}
									onChange={(event) =>
										updateActionData((data) => ({ ...data, envValue: event.target.value }))
									}
									placeholder="value"
									clearable
								/>
							) : null}
						</>
					)}

					{draft?.kind === "control" && draft.data.moduleType === "condition" && (
						<TextInputField
							label={t("workflow.node.conditionExpression")}
							value={draft.data.conditionExpression ?? ""}
							onChange={(event) =>
								updateControlData((data) => ({
									...data,
									conditionExpression: event.target.value,
								}))
							}
							placeholder="ex) inputs.ai-1.score > 0.8"
							clearable
						/>
					)}

					{draft?.kind === "control" && draft.data.moduleType === "loop" && (
						<>
							<TextInputField
								label={t("workflow.node.loopExpression")}
								value={draft.data.loopExpression ?? ""}
								onChange={(event) =>
									updateControlData((data) => ({
										...data,
										loopExpression: event.target.value,
									}))
								}
								placeholder="ex) stepOutputs.loadClasses.rows"
								clearable
							/>
							<TextInputField
								label="Loop alias"
								value={draft.data.loopAlias ?? "item"}
								onChange={(event) =>
									updateControlData((data) => ({
										...data,
										loopAlias: event.target.value,
									}))
								}
								placeholder="item"
								clearable
							/>
						</>
					)}

					{draft?.kind === "control" && draft.data.moduleType === "delay" && (
						<TextInputField
							label={t("workflow.node.delayMs")}
							type="number"
							value={draft.data.delayMs ?? 0}
							onChange={(event) =>
								updateControlData((data) => ({
									...data,
									delayMs: Number(event.target.value),
								}))
							}
							placeholder="1000"
						/>
					)}

					{draft?.kind === "control" && draft.data.moduleType === "schedule" && (
						<TextInputField
							label={t("workflow.node.scheduleCron")}
							value={draft.data.scheduleCron ?? ""}
							onChange={(event) =>
								updateControlData((data) => ({
									...data,
									scheduleCron: event.target.value,
								}))
							}
							placeholder="* * * * *"
							clearable
						/>
					)}
				</VStack>

				<HStack className="w-full gap-[10px] pt-[10px]">
					<Button className="w-full" onClick={handleAttemptClose} disabled={isSaveDisabled}>
						{t("common.cancel")}
					</Button>
					<Button
						className="w-full"
						variant="primary"
						onClick={handleSave}
						disabled={isSaveDisabled}
					>
						{t("common.confirm")}
					</Button>
				</HStack>
			</VStack>
		</Modal>
	);
};
